import { ActionList, ActionMenu } from '@primer/react';
import { sortReleases } from '@upright/common/model/releases';
import { E2eTestElementId } from '@upright/test-ids/dom-elements';
import { useGetAvailableReleases } from '@upright/ui/api/model/releases';
import { usePushGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { useGetLoggedInUser } from '@upright/ui/common/generic/hooks';
import { useGetDefaultVersionOfRelease } from '@upright/ui/common/upright/model/output/releases/hooks';
import { friendlyReleaseLabel } from '@upright/ui/common/upright/model/output/releases/options';
import { Collection, Logout, OfficeWorkerStreamlineCore } from '@upright/ui/icons';
import { logout, setModelVersionAndRelease } from '@upright/ui/untidy/redux/actions/actions';
import { getVersionAndRelease } from '@upright/ui/untidy/redux/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styles from './unified-top-bar.module.css';

/**
 * The account menu in the unified shell's top bar: the 32px initials square, holding
 * Account, the release selector, Log out and the legal links.
 *
 * Every row here is one a customer sees. The team-only settings (View settings, Value set,
 * the product-data version selector) are in the internal-tools start menu at the bottom
 * left instead, which is mounted at the app root and so renders inside this shell too.
 * *Why:* internal tooling has one home, so a team user has one place to look, and this menu
 * reads the same for a team account as it does for a customer's (PLT-6732).
 *
 * The release selector is not team-only and stays: it is a submenu rather than an inline
 * group, the PLT-6531 placement ("settings-like, rarely changed").
 *
 * What the dark header's user menu had and this does not: Contact and Knowledge base, which
 * are the Help menu beside this one. The legal links it reached through *About Upright
 * Platform* are the footer below.
 *
 * The initials, not the name: the design system's account slot is the 32px square
 * (design/50-components.md "Avatar"), and the name is in the button's label for assistive
 * technology.
 */
export function UnifiedAccountMenu({
  release = true,
  companyDataAttribution = true,
  person,
}: {
  release?: boolean;
  companyDataAttribution?: boolean;
  person?: SignedInPerson;
}) {
  const user = useGetLoggedInUser();
  const dispatch = useDispatch();

  if (person) return <SignedInPersonMenu person={person} companyDataAttribution={companyDataAttribution} />;
  if (!user) {
    return null;
  }

  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username;

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <button
          type="button"
          className={styles.accountWrap}
          data-testid={E2eTestElementId.USER_DROPDOWN}
          aria-label={`Account: ${displayName}`}
        >
          <span className={styles.account} aria-hidden="true">
            {getInitials(user.firstName, user.lastName, user.username)}
          </span>
        </button>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="medium" align="end">
        <ActionList>
          <ActionList.LinkItem as={Link} to={`/user/${user.username}/details`}>
            <ActionList.LeadingVisual>
              <OfficeWorkerStreamlineCore width={20} height={20} />
            </ActionList.LeadingVisual>
            Account
          </ActionList.LinkItem>
          {release ? <ReleaseSubmenu /> : null}
          <ActionList.Divider />
          {/*
            A neutral row, not `danger`. Logging out destroys nothing and is undone by logging back
            in, and red here says it is the riskiest thing in the menu (design/50-components.md
            "Danger describes destruction, not emphasis").
          */}
          <ActionList.Item
            onSelect={() => {
              dispatch(logout());
            }}
          >
            <ActionList.LeadingVisual>
              <Logout width={20} height={20} />
            </ActionList.LeadingVisual>
            Log out
          </ActionList.Item>
        </ActionList>
        <LegalFooter companyDataAttribution={companyDataAttribution} />
      </ActionMenu.Overlay>
    </ActionMenu>
  );
}

/**
 * A person signed in with an identity other than the platform's: the Arvo console on Eevo's own
 * identity (PLT-7477). They have no platform account page or release, so the menu holds only
 * signing out and the legal lines.
 */
export interface SignedInPerson {
  name: string | null;
  email: string;
  onSignOut: () => void;
}

function SignedInPersonMenu({
  person,
  companyDataAttribution,
}: {
  person: SignedInPerson;
  companyDataAttribution: boolean;
}) {
  const [first, ...rest] = (person.name ?? '').trim().split(/\s+/).filter(Boolean);
  const label = person.name?.trim() || person.email;
  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <button type="button" className={styles.accountWrap} aria-label={`Account: ${label}`}>
          <span className={styles.account} aria-hidden="true">
            {getInitials(first, rest.at(-1), person.email)}
          </span>
        </button>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="medium" align="end">
        <ActionList>
          <ActionList.Item onSelect={person.onSignOut}>
            <ActionList.LeadingVisual>
              <Logout width={20} height={20} />
            </ActionList.LeadingVisual>
            Sign out
            <ActionList.Description variant="block">{person.email}</ActionList.Description>
          </ActionList.Item>
        </ActionList>
        <LegalFooter companyDataAttribution={companyDataAttribution} />
      </ActionMenu.Overlay>
    </ActionMenu>
  );
}

/**
 * The legal lines, as the account menu's footer rather than as rows in it: they are read once
 * and never chosen between, so they take the incidental-meta step (12px, `gray-600`) and sit
 * below everything the menu is actually for. Their previous home was the *About Upright
 * Platform* modal, which was the mobile footer in a dialog frame and is not in this shell.
 *
 * Outside the `ActionList` on purpose. Every child of that list is a menu item: as rows these
 * four links would join the arrow-key cycle and put "Privacy policy" one keypress from "Log
 * out". Here they are ordinary links, reachable by Tab and skipped by the item navigation.
 *
 * The Demandbase line is an attribution for third-party company data, not a nicety, so it comes
 * along with the links rather than being dropped with the rest of the modal's contents. A console
 * that shows no Demandbase data (the Arvo console on eevo.io) has nothing to attribute and leaves
 * it out.
 */
function LegalFooter({ companyDataAttribution }: { companyDataAttribution: boolean }) {
  return (
    <div className={styles.legalFooter}>
      <div className={styles.legalLinks}>
        <a
          className={styles.legalLink}
          href="https://www.uprightproject.com/terms-of-use"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of use
        </a>
        <a
          className={styles.legalLink}
          href="https://www.uprightproject.com/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy policy
        </a>
      </div>
      {companyDataAttribution ? (
        <p className={styles.legalNote}>
          Company data from{' '}
          <a className={styles.legalLink} href="https://www.demandbase.com/" target="_blank" rel="noopener noreferrer">
            Demandbase
          </a>
        </p>
      ) : null}
    </div>
  );
}

/**
 * The release selector as a submenu: one row in the account menu naming the current
 * release, opening the list beside it. A submenu rather than a group inline, because a team
 * user sees every release there has ever been — twenty-odd rows, which inline pushed Log out
 * to the bottom of the viewport and the overlay up over the bar. The list scrolls past the
 * overlay's `large` height.
 *
 * Selecting a release sets the release and its default product-data version together, and
 * records the change.
 */
function ReleaseSubmenu() {
  const dispatch = useDispatch();
  const pushGtmEvent = usePushGtmEvent();
  const getVersionOfRelease = useGetDefaultVersionOfRelease();
  const { release } = useSelector(getVersionAndRelease);
  const { data: releases } = useGetAvailableReleases(sortReleases);

  if (!releases || releases.length === 0) {
    return null;
  }
  const current = releases.find(candidate => candidate.number === release);

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <ActionList.Item>
          <ActionList.LeadingVisual>
            <Collection width={20} height={20} />
          </ActionList.LeadingVisual>
          Release
          <ActionList.Description variant="inline">
            {current ? friendlyReleaseLabel(current) : release}
          </ActionList.Description>
        </ActionList.Item>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="small" maxHeight="large">
        <ActionList selectionVariant="single">
          {releases.map(candidate => (
            <ActionList.Item
              key={candidate.number}
              selected={candidate.number === release}
              onSelect={() => {
                if (candidate.number === release) {
                  return;
                }
                const version = getVersionOfRelease(candidate.number);
                if (version) {
                  dispatch(setModelVersionAndRelease(version, candidate.number));
                }
                pushGtmEvent({ event: CustomGtmEvent.RELEASE_CHANGE, data: { selectedRelease: candidate.number } });
              }}
            >
              {friendlyReleaseLabel(candidate)}
            </ActionList.Item>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  );
}

function getInitials(firstName?: string, lastName?: string, username?: string) {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
}
